import { Observable, BehaviorSubject, take, filter, forkJoin } from 'rxjs';
import eventAttributes from '../events/EventAttributes.mjs';
import invoiceTypes from './InvoiceTypes.mjs';
import productFeatures from '../events/ProductFeatures.mjs';
import serviceStates from './ServiceStates.mjs';
import permissions from './Permissions.mjs';

/**
 * @module DeviceService
 */
function createDeviceService(
    apiService,
    userService,
    logService,
    util,
    _window,
    environment
) {
    const _deviceService = {};
    const productsPrefix = 'products';
    const myqCloud ='myQCloud';
    const expireMessageDays = 3;
    const aboutToExpireDays = 3;

    // subjects hold the current values of all data
    const deviceSubjects = {};
    const productInfoSubjects = {};
    const filterItemSubjects = {};
    const accessLevelsSubject = new BehaviorSubject();
    const deviceUiSettingsSubject = new BehaviorSubject();
    const eventInfoSubject = new BehaviorSubject();
    const timezoneSubject = new BehaviorSubject();

    const lightweight = {};
    const heavyweight = {};
    const requestedServices = {};
    const requestingServices = {};
    const gotDevices = {};
    const gotServices = {};
    const colorRegex = /^[0-9A-F]{6}$/i;
    const setColorInProgress = {};

    let accessLevelsInProgress;
    let deviceUiSettingsInProgress;
    let eventInfoInProgress;
    let timezoneInProgress;
    let selectedDevice;

    const garageProductFeatures = Object.freeze([
        productFeatures.GARAGE_ANALYSIS,
        productFeatures.GARAGE_ANALYSIS_POC1,
        productFeatures.GARAGE_ANALYSIS_POC2,
        productFeatures.GARAGE_ANALYSIS_POC3
    ]);

    const freeServiceProductFeatures = Object.freeze([
        productFeatures.VIDEO_DVR,
        productFeatures.SNAPSHOT_DVR,
        productFeatures.PEOPLE_DETECT,
        productFeatures.LMO_TRACK,
        productFeatures.HIGHLIGHT_VIDEO
    ]);

    const billingTypes = Object.freeze({
        subscription: 'Subscription',
        prepay: 'Prepay',
        trial: 'Trial',
        comp: 'Comp',
        voucher: 'Voucher'
    });

    const contextEnum = Object.freeze({
        all: 'all',
        list: 'list',
        forEvents: 'forEvents'
    });

    const modelsWithResolutionNames = ',stuartcam,minioncam,lynxindoor,lynxoutdoor,';
    const partnersWithResolutionNames = ',minions,tend,';

    const endOfLifeConfig = [];

    // const endOfLifeConfig = [
    //     {
    //         partners: [ 'swann', 'kodak', 'seedonk', 'minions', 'tend', 'deere', 'belkin', 'bthome', 'heathco', 'etiger' ],
    //         startDate: new _window.Date('2021-11-01T00:00:00').getTime(),
    //         endDate: new _window.Date('2022-02-14T00:00:00').getTime(),
    //         status: 'warning'
    //     },
    //     {
    //         partners: [ 'swann', 'kodak', 'seedonk', 'minions', 'tend', 'deere', 'belkin', 'bthome', 'heathco', 'etiger' ],
    //         startDate: new _window.Date('2022-02-14T00:00:00').getTime(),
    //         endDate: undefined,
    //         status: 'ended'
    //     }
    // ];

    function getEolInfo(device) {
        const eolConfig = endOfLifeConfig.find(config => {
            if (config.partners.find(p => device?.hw && device?.hw?.partnerId?.toLowerCase() === p.toLowerCase())) {
                const now = _window.Date.now();
                return (!config.startDate && config.endDate > now)
                        || (!config.endDate && config.startDate <= now)
                        || (config.startDate <= now && config.endDate > now);
            }
            return false;
        });
        if (eolConfig) {
            device.eolInfo = util.merge({}, eolConfig);
        }
    }

    function log(params) {
        params.module = 'deviceService';
        logService.log(params);
        return params;
    }

    /**
     * @this {Object} an object with the ownerAlias to match
     * @param {Device} device
     * @returns {Boolean} true if the device matches this.ownerAlias or there is no this.ownerAlias
     */
    function byOwnerAlias(device) {
        if (!this.ownerAlias) {
            return true;
        }
        return util.aliasWithNoGuidPrefix(this.ownerAlias) === util.aliasWithNoGuidPrefix(device.ownership.ownerAlias);
    }

    /**
     * @this {Object} an object with the context to match
     * @param {Device} device
     * @returns {Boolean} true if the device matches id.context or there is no id.context
     */
    function byContext(device) {
        if (this.context === contextEnum.forEvents) {
            return (!device.status.deleted || device.status.hasEvents)
                && _deviceService.hasPermission(device, permissions.eventsList);
        } else if (this.context === contextEnum.list) {
            return !device.status.deleted;
        }
        return true;
    }

    /**
     * @description Sorts devices by their display name for consistent UI
     * @param {Device} a first device to compare for sorting
     * @param {Device} b second device to compare for sorting
     */
    function byDisplayName(a, b) {
        if (a.settings.displayName > b.settings.displayName) {
            return 1;
        }
        if (a.settings.displayName < b.settings.displayName) {
            return -1;
        }
        return 0;
    }

    /**
     * @description having analytics means some kind of detection or tracking capability,
     *     such as PEOPLE_DETECT or LMO_TRACK, but this function tries to cover more general
     *     cases in case newer services are added later. As a newer standard,
     *     service names that start with Analyze.Video do detection.
     * @param {String} service name or id of the service
     * @returns {Boolean} true if the service provides analytics
     */
    function serviceHasAnalytics(service) {
        return service.endsWith('_DETECT')
            || service.endsWith('_TRACK')
            || service.startsWith('Analyze.Video.');
    }

    function setServiceStateInfo(device, state, product) {
        device.invoiceType = product.invoiceType;
        device.serviceState = state;
        if (product.endDttm) {
            device.serviceEndDate = new _window.Date(product.endDttm);
        }
        return true;
    }

    function calcServiceStateForExpired(device) {
        const products = [];
        Object.keys(device.serviceStatus.expired).forEach(item => products.push(device.serviceStatus.expired[item]));

        let product;
        product = products.find(p => p.invoiceType === invoiceTypes.prepay || p.invoiceType === invoiceTypes.voucher);
        if (product?.endDttm) {
            const expireMessageDate = new _window.Date(product.endDttm);
            expireMessageDate.setDate(expireMessageDate.getDate() + expireMessageDays);
            if (new _window.Date() <= expireMessageDate) {
                return setServiceStateInfo(device, serviceStates.expired, product);
            }
        }

        product = products.find(p => p.invoiceType === invoiceTypes.trial);
        if (product?.endDttm) {
            const expireMessageDate = new _window.Date(product.endDttm);
            expireMessageDate.setDate(expireMessageDate.getDate() + expireMessageDays);

            if (new _window.Date() <= expireMessageDate) {
                return setServiceStateInfo(device, serviceStates.trialExpired, product);
            }
        }

        product = products.find(p => p.invoiceType === invoiceTypes.comp);
        if (product?.endDttm) {
            const expireMessageDate = new _window.Date(product.endDttm);
            expireMessageDate.setDate(expireMessageDate.getDate() + expireMessageDays);

            if (new _window.Date() <= expireMessageDate) {
                return setServiceStateInfo(device, serviceStates.freeExpired, product);
            }
        }
    }

    function calcServiceStateForAboutToExpire(device, products) {
        return !products.every(product => {
            if (product.endDttm) {
                let aboutToExpireDate = new _window.Date(product.endDttm);
                aboutToExpireDate.setDate(aboutToExpireDate.getDate() - aboutToExpireDays);

                if (new _window.Date() > aboutToExpireDate) {

                    if (product.invoiceType === invoiceTypes.main) {
                        return !setServiceStateInfo(device, serviceStates.autoRenew, product);
                    }

                    if (product.invoiceType === invoiceTypes.singleCamSub) {
                        return !setServiceStateInfo(device, serviceStates.singleCamSub, product);
                    }

                    if (product.invoiceType === invoiceTypes.prepay
                            || product.invoiceType === invoiceTypes.voucher) {
                        return !setServiceStateInfo(device, serviceStates.aboutToExpire, product);
                    }

                    if (product.invoiceType === invoiceTypes.trial) {
                        return !setServiceStateInfo(device, serviceStates.trialAboutToExpire, product);
                    }

                    if (product.invoiceType === invoiceTypes.comp) {
                        return !setServiceStateInfo(device, serviceStates.freeAboutToExpire, product);
                    }
                }
            }
            return true;
        });
    }

    function productByInvoiceType (products, invoiceType, features) {
        return products.find(product => {
            if (product.invoiceType === invoiceType) {
                if (!features) {
                    return true;
                }
                if (features.indexOf(product.serviceType) > -1) {
                    return true;
                }
            }
            return false;
        });
    }

    function calcServiceStateForCurrent(device, products) {
        let product;

        product = productByInvoiceType(products, invoiceTypes.main);
        if (product) {
            return setServiceStateInfo(device, serviceStates.autoRenew, product);
        }

        product = productByInvoiceType(products, invoiceTypes.singleCamSub);
        if (product) {
            return setServiceStateInfo(device, serviceStates.singleCamSub, product);
        }

        product = productByInvoiceType(products, invoiceTypes.prepay);
        if (product) {
            return setServiceStateInfo(device, serviceStates.expires, product);
        }

        product = productByInvoiceType(products, invoiceTypes.voucher);
        if (product) {
            return setServiceStateInfo(device, serviceStates.expires, product);
        }

        product = productByInvoiceType(products, invoiceTypes.trial);
        if (product) {
            return setServiceStateInfo(device, serviceStates.trialExpires, product);
        }

        product = productByInvoiceType(products, invoiceTypes.comp);
        if (product) {
            if (product.endDttm) {
                return setServiceStateInfo(device, serviceStates.freeExpires, product);
            }
            return setServiceStateInfo(device, serviceStates.freeService, product);
        }

        // only set free service message for certain features
        product = productByInvoiceType(products, invoiceTypes.freeService, freeServiceProductFeatures);
        if (product) {
            return setServiceStateInfo(device, serviceStates.freeService, product);
        }
    }

    function calcServiceState(device) {
        if (device.serviceStatus && device.serviceStatus.expired) {
            if (calcServiceStateForExpired(device)) {
                return;
            }
        }

        if (device.serviceStatus && device.serviceStatus.current) {
            const products = [];
            Object.keys(device.serviceStatus.current).forEach(item =>
                products.push(device.serviceStatus.current[item])
            );
            if (products.length > 0 && calcServiceStateForAboutToExpire(device, products)) {
                return;
            }
            calcServiceStateForCurrent(device, products);
        }
    }

    function caculateProductFeaturesforDevice(device, productsArrayName) {
        if (!Array.isArray(device[productsArrayName])) {
            device[productsArrayName] = [];
        }

        device[productsArrayName].forEach(productObj => {
            productObj.product.features.forEach(feature => {

                const existingFeature = device.availableFeatures[feature.serviceType];
                if (existingFeature) {
                    // there is already a feature here, so keep the one with the greatest val1
                    if (typeof existingFeature.val1 === 'number'
                            && typeof feature.val1 === 'number'
                            && existingFeature.val1 < feature.val1) {
                        // new feature is "bigger", so replace existing feature
                        device.availableFeatures[feature.serviceType] = feature;
                    }
                } else {
                    device.availableFeatures[feature.serviceType] = feature;
                }

            });
        });
    }

    function caculateProductFeatures(device) {
        util.clear(device.availableFeatures);
        Object.keys(billingTypes).forEach(key => caculateProductFeaturesforDevice(device, productsPrefix + billingTypes[key]));

        // summary of features just calculated - basically shortcuts for UI
        device.uiFavorites.hasService = _deviceService.hasActiveService(device, productFeatures.FAVORITES);
        device.uiFavorites.isServicePossible = (device.availableFeatures[productFeatures.FAVORITES] !== undefined)
            || device.uiFavorites.hasService;
    }

    function getEventInfo(params) {
        if ((!eventInfoSubject.getValue() || params?.refresh) && !eventInfoInProgress) {
            eventInfoInProgress = true;
            apiService.events.deviceIdsWithEvents(params).then(
                result => {
                    eventInfoInProgress = undefined;
                    if (Array.isArray(result?.data?.items)) {
                        eventInfoSubject.next(result.data.items);
                    } else {
                        clearSubject(eventInfoSubject);
                    }
                },
                e => {
                    eventInfoInProgress = undefined;
                    clearSubject(eventInfoSubject);
                    log({
                        code: 'getEventInfoFailed',
                        descr: 'getting deviceIdsWithEvents failed',
                        error: e,
                        traceId: e?.traceId
                    });
                }
            );
        }
        return eventInfoSubject.asObservable();
    }

    /**
     * @returns {Observable} emits array of access levels
     */
    function getAccessLevels(params) {
        if (!accessLevelsSubject.getValue() && !accessLevelsInProgress) {
            accessLevelsInProgress = true;
            apiService.devices.accessLevels(params).then(
                result => {
                    accessLevelsInProgress = false;
                    if (Array.isArray(result?.data?.items)) {
                        accessLevelsSubject.next(result.data.items);
                    } else {
                        clearSubject(accessLevelsSubject);
                    }
                },
                e => {
                    accessLevelsInProgress = false;
                    clearSubject(accessLevelsSubject);
                    log({
                        code: 'getAccessLevelsFailed',
                        descr: 'getting accessLevels failed',
                        error: e,
                        traceId: e?.traceId
                    });
                }
            );
        }
        return accessLevelsSubject.asObservable();
    }

    function getDeviceUiSettings(params) {
        if ((!deviceUiSettingsSubject.getValue() || params?.refresh) && !deviceUiSettingsInProgress) {
            deviceUiSettingsInProgress = true;
            apiService.uiSettings.getForDevices(params).then(
                result => {
                    deviceUiSettingsInProgress = false;
                    if (Array.isArray(result?.data?.devices)) {
                        deviceUiSettingsSubject.next(result.data.devices);
                    } else {
                        clearSubject(deviceUiSettingsSubject);
                    }
                },
                e => {
                    deviceUiSettingsInProgress = false;
                    clearSubject(deviceUiSettingsSubject);
                    log({
                        code: 'deviceUiSettingsFailed',
                        descr: 'getting deviceUiSettings failed',
                        error: e,
                        traceId: e?.traceId
                    });
                }
            );
        }
        return deviceUiSettingsSubject.asObservable();
    }

    function resolutionSort(a, b) {
        if (a.h < b.h) {
            return 1;
        }
        if (a.h > b.h) {
            return -1;
        }
        return 0;
    }

    /**
     * @description assign uiSettings for a device
     * @param {Device} device
     * @param {Object} uiSettings
     */
    function processDeviceFromUiSettings(device, uiSettings) {
        const uiSettingItem = uiSettings.find(d => d.deviceId === device.id);
        if (uiSettingItem) {
            util.merge(device.uiSettings, uiSettingItem.uiSettings);
        }
    }

    function storeDevice(device, devices) {
        let existing = devices.find(util.byId, { id: device.id });
        if (!existing) {
            // add new
            devices.push(device);
            existing = device;
        } else {
            // modify existing
            existing.lastUpdate = device.lastUpdate;
            util.merge(existing.cap, device.cap);
            util.merge(existing.settings, device.settings);
            util.merge(existing.status, device.status);
            util.merge(existing.ownership, device.ownership);

            // update wifiStrength without overwriting an existing value with undefined
            if (device?.net?.eth?.wifiStrength !== undefined) {
                existing.net.eth.wifiStrength = device.net.eth.wifiStrength;
            }

            util.clear(existing.permissions);
            util.merge(existing.permissions, util.merge({}, device.permissions));
        }
        return existing;
    }

    function processDevicePermissions(device, accessLevels, roles) {
        const accessLevel = accessLevels.find(a => {
            if (a && a !== null) {
                return a.deviceId === device.id;
            }
            return undefined;
        });
        if (roles?.[accessLevel?.roleId]?.permissions) {
            device.permissions = util.merge({}, roles[accessLevel.roleId].permissions);
        } else {
            device.permissions = { noPermissions: true };
        }
    }

    function processDevice(devices, rawDevice, user, uiSettings, eventInfo, accessLevels, roles, now) {
        // Add all properties that device could ever have to optimize the browser JS engine
        const device = {
            availableFeatures: {},
            cap: rawDevice.cap,
            hw: rawDevice.hw,
            net: {
                eth: {
                    wifiStrength: rawDevice?.net?.eth?.wifiStrength
                }
            },
            id: rawDevice.id,
            invoiceType: undefined,
            lastUpdate: now,
            ownership: rawDevice.ownership ?? {},
            productsPrepay: undefined,
            productsTrial: undefined,
            productsComp: undefined,
            productsVoucher: undefined,
            productsSubscription: undefined,
            serviceEndDate: undefined,
            serviceState: undefined,
            serviceStatus: undefined,
            settings: rawDevice.settings ?? {},
            status: util.merge(
                rawDevice.status ?? {},
                { hasEvents: eventInfo.indexOf(rawDevice.id) !== -1 }
            ),
            type: rawDevice.type,
            uiFavorites: { hasService: undefined, isServicePossible: undefined },
            uiSettings: {},
            permissions: undefined,
            eolInfo: undefined
        };

        const existing = storeDevice(device, devices);
        processDevicePermissions(existing, accessLevels, roles);
        processDeviceFromUiSettings(existing, uiSettings);
        getEolInfo(existing);
        existing?.cap?.cam?.resList?.sort(resolutionSort);
    }

    /**
     * @param {Array} devicesResult array of raw devices from the API
     * @param {Object} params request parameters
     * @returns {Array} imported devices with all calculations done
     */
    function importDevices(devicesResult, params) {
        const now = _window.Date.now();

        if (!devicesResult) {
            return;
        }

        forkJoin({
            user: userService.get(params).pipe(util.takeFirstExistingWithNoError),
            deviceUiSettings: getDeviceUiSettings(params).pipe(util.takeFirstExistingWithNoError),
            eventInfo: getEventInfo(params).pipe(util.takeFirstExistingWithNoError),
            accessLevels: getAccessLevels(params).pipe(util.takeFirstExistingWithNoError),
            roles: userService.roles(params).pipe(util.takeFirstExistingWithNoError)
        }).subscribe({
            next: result => {
                let hasError = false;
                Object.keys(result).forEach(key => {
                    if (result[key].isError) {
                        log({
                            code: 'importDevices',
                            descr: 'importDevices failed to get requirement ' + key,
                            object: devicesResult.map(device => device.id),
                            error: result[key].error,
                            traceId: params.traceId
                        });
                        hasError = true;
                    }
                });
                if (hasError) {
                    return;
                }

                const devices = getSubject().getValue() ?? [];

                devicesResult.forEach(rawDevice =>
                    processDevice(
                        devices,
                        rawDevice,
                        result.user,
                        result.deviceUiSettings,
                        result.eventInfo,
                        result.accessLevels,
                        result.roles,
                        now
                    )
                );
                if (params?.id) {
                    getSubject({ id: params.id }).next(devices.find(d => d.id === params.id));
                    devices.sort(byDisplayName);
                    getSubject().next(devices);
                    gotDevices[params.id] = true;
                } else {
                    devices.forEach(device => getSubject({ id: device.id }).next(device));
                    devices.sort(byDisplayName);
                    getSubject().next(devices);
                    //removeUnusedUiSettings(result.deviceUiSettings, devicesResult);
                    gotDevices[contextEnum.all] = true;
                }
                importServices(params);
            },
            error: util.noop
        });
    }

    function importServices(params) {
        if (params?.id && gotDevices[params.id] && requestingServices[params.id] && !requestedServices[params.id]) {
            const device = getSubject({ id: params.id }).getValue();
            if (device) {
                requestedServices[device.id] = true;
                if (_deviceService.hasPermission(device, permissions.premiumService)) {
                    serviceCalls(device, params).then(
                        deviceResult => {
                            gotServices[device.id] = true;
                            getSubject({ id: deviceResult.id }).next(deviceResult);
                        },
                        () => {
                            delete gotServices[device.id];
                            delete requestedServices[device.id];
                        }
                    );
                }
            }
        } else if (gotDevices[contextEnum.all] && requestingServices[contextEnum.all] && !requestedServices[contextEnum.all]) {
            const deviceServices = [];
            const devices = getSubject().getValue();
            if (Array.isArray(devices)) {
                devices.forEach(device => {
                    if (device.id && !requestedServices[device.id]) {
                        requestedServices[device.id] = true;
                        if (_deviceService.hasPermission(device, permissions.premiumService)) {
                            deviceServices.push(
                                serviceCalls(device, params).then(
                                    deviceResult => {
                                        gotServices[device.id] = true;
                                        getSubject({ id: deviceResult.id }).next(deviceResult);
                                        return Promise.resolve();
                                    },
                                    () => {
                                        delete gotServices[device.id];
                                        delete requestedServices[device.id];
                                        return Promise.reject();
                                    }
                                )
                            );
                        }
                    }
                });
            }
            requestedServices[contextEnum.all] = true;
            Promise.all(deviceServices).then(
                function() {
                    gotServices[contextEnum.all] = true;
                    getSubject().next(devices);
                },
                function() {
                    delete gotServices[contextEnum.all];
                    delete requestedServices[contextEnum.all];
                }
            );
        }
    }

    // makes API calls for devices
    function getDeviceData(params) {
        if (params?.id) {
            apiService.devices.get({
                id: params.id,
                lightweight: (params.heavy ? undefined : true),
                guestCode: params.guestCode,
                traceId: params.traceId
            }).then(
                deviceGetResult => {
                    importDevices([ deviceGetResult.data ], params);
                },
                () => {
                    if (params.heavy) {
                        delete heavyweight[params.id];
                    } else {
                        delete lightweight[params.id];
                    }
                }
            );
        } else {
            apiService.devices.get({
                lightweight: (params.heavy ? undefined : true),
                guestCode: params.guestCode,
                traceId: params.traceId
            }).then(
                deviceGetResult => {
                    importDevices(deviceGetResult.data.items, params);
                },
                () => {
                    if (params.heavy) {
                        delete heavyweight[contextEnum.all];
                    } else {
                        delete lightweight[contextEnum.all];
                    }
                }
            );
        }
    }

    function importServiceProducts(device, serviceProductsResult) {
        // init array
        if (!Array.isArray(device[productsPrefix + serviceProductsResult.config.billingType])) {
            device[productsPrefix + serviceProductsResult.config.billingType] = [];
        } else {
            device[productsPrefix + serviceProductsResult.config.billingType].splice(0);
        }

        // add product information
        serviceProductsResult.data.list.forEach(product =>
            device[productsPrefix + serviceProductsResult.config.billingType].push(product)
        );
    }

    function couldHaveFreeTrial(device) {
        return device?.hw.partnerId !== 'Tend';
    }

    function couldHavePrepay(device) {
        return device?.hw.partnerId !== 'Tend';
    }

    function tendServiceCalls(device, apiCalls, params) {
        apiCalls.push(
            apiService.services.getProductsForDevice({
                deviceId: device.id,
                paymentProcessorId: util.getTendPaymentProcessorId(),
                billingType: billingTypes.subscription,
                traceId: params?.traceId
            })
        );
        if (couldHavePrepay(device)) {
            apiCalls.push(
                apiService.services.getProductsForDevice({
                    deviceId: device.id,
                    paymentProcessorId: util.getTendPaymentProcessorId(),
                    billingType: billingTypes.prepay,
                    traceId: params?.traceId
                })
            );
        }
        if (couldHaveFreeTrial(device)) {
            apiCalls.push(
                apiService.services.getProductsForDevice({
                    deviceId: device.id,
                    paymentProcessorId: 'Trial',
                    billingType: billingTypes.trial,
                    traceId: params?.traceId
                })
            );
        }
    }

    function myqServiceCalls(device, apiCalls, params) {
        apiCalls.push(
            apiService.services.getProductsForDevice({
                deviceId: device.id,
                paymentProcessorId: myqCloud,
                billingType: billingTypes.subscription,
                traceId: params?.traceId
            })
        );
        // Trials are not working on the server right now,
        // so do not try to get them.
    }

    function serviceCalls(device, params) {
        const apiCalls = [];
        apiCalls.push(
            apiService.services.getFullStatusForDevice({
                deviceId: device.id,
                traceId: params?.traceId
            }).then(
                result => result,
                e => e
            )
        );

        if (environment.partnerId === 'myQ'
                || environment.partnerId === 'myQBusiness') {
            myqServiceCalls(device, apiCalls, params);
        } else if (environment.partnerId === 'TendSecure'
                || environment.partnerId === 'iSecurityPlus') {
            tendServiceCalls(device, apiCalls, params);
        }

        // grouping API calls with Promise.all saves UI rendering cycles
        return Promise.all(apiCalls).then(
            importData => {
                if (device) {
                    importData.forEach((item, index) => {
                        if (index === 0) {
                            device.serviceStatus = item.data;
                        } else {
                            importServiceProducts(device, item);
                        }
                    });
                    caculateProductFeatures(device);
                    if (device.serviceStatus) {
                        calcServiceState(device);
                    }

                    // log any unknown product features
                    let featureKeys = [];
                    if (device.serviceStatus && device.serviceStatus.current) {
                        featureKeys = featureKeys.concat(Object.keys(device.serviceStatus.current));
                    }
                    featureKeys = featureKeys.concat(Object.keys(device.availableFeatures));
                    featureKeys.forEach(feature => {
                        if (!productFeatures[feature]) {
                            if (Math.random() <= 0.01) {
                                log({
                                    code: 'unknownProductFeature',
                                    object: { feature: feature },
                                    deviceId: device.id,
                                    traceId: params?.traceId
                                });
                            }
                        }
                    });

                }
                return device;
            },
            e => {
                log({
                    code: 'serviceDataFailure',
                    descr: 'failed to get service calls',
                    deviceId: device?.id,
                    traceId: params?.traceId,
                    error: e
                });
                delete requestedServices[device.id];
                return Promise.reject();
            }
        );
    }

    function updateSelectedDevice(device) {
        selectedDevice = device;
    }

    function addFilterItem(arr, eventAttribute, enabled) {
        if (eventAttribute && arr) {
            arr.push(util.merge({ enabled: enabled }, eventAttribute));
        }
    }

    /**
     * @param {Array<Devices>} devices An array of devices for calculating filterItems for.
     *     The devices should be pre-filtered.
     * @returns {Object} filterItems object
     */
    function calculateFilterItems(devices) {
        const filterItems = {};

        // SMART DETECTION
        const enableSmart = Boolean(anyHasSmartDetectionService(devices));
        const displaySmart = Boolean(anyHasSmartDetectionCapability(devices));

        filterItems.smart = {
            items: []
        };

        addFilterItem(
            filterItems.smart.items,
            eventAttributes.person,
            enableSmart || displaySmart
        );

        addFilterItem(
            filterItems.smart.items,
            eventAttributes.object,
            enableSmart || displaySmart
        );

        addFilterItem(
            filterItems.smart.items,
            eventAttributes.pet,
            enableSmart || displaySmart
        );

        addFilterItem(
            filterItems.smart.items,
            eventAttributes.car,
            enableSmart || displaySmart
        );

        addFilterItem(
            filterItems.smart.items,
            eventAttributes.baby,
            enableSmart || displaySmart
        );

        addFilterItem(
            filterItems.smart.items,
            eventAttributes.none,
            enableSmart || displaySmart
        );

        // PROPS
        const hasDeliveryService = anyHasActiveFeatures(
            devices,
            [ productFeatures.DELIVERY_DVR ]
        );
        const canGetDeliveryService = anyHasFeatureCapabilities(
            devices,
            [ productFeatures.DELIVERY_DVR ]
        );
        const hasGarageService = anyHasActiveFeatures(
            devices,
            garageProductFeatures
        );
        const canGetGarageService = anyHasFeatureCapabilities(
            devices,
            garageProductFeatures
        );

        filterItems.props = {
            items: []
        };

        addFilterItem(
            filterItems.props.items,
            eventAttributes.delivery,
            hasDeliveryService || canGetDeliveryService
        );

        addFilterItem(
            filterItems.props.items,
            eventAttributes.garageOpen,
            hasGarageService || canGetGarageService
        );

        addFilterItem(
            filterItems.props.items,
            eventAttributes.garageClose,
            hasGarageService || canGetGarageService
        );

        addFilterItem(
            filterItems.props.items,
            eventAttributes.pin,
            hasGarageService || canGetGarageService
        );

        addFilterItem(
            filterItems.props.items,
            eventAttributes.call,
            hasGarageService || canGetGarageService
        );

        addFilterItem(
            filterItems.props.items,
            eventAttributes.autoSecure,
            hasGarageService || canGetGarageService
        );

        addFilterItem(
            filterItems.props.items,
            eventAttributes.animal,
            hasGarageService || canGetGarageService
        );
        addFilterItem(
            filterItems.props.items,
            eventAttributes.vehicle,
            hasGarageService || canGetGarageService
        );

        addFilterItem(
            filterItems.props.items,
            eventAttributes.package,
            hasGarageService || canGetGarageService
        );

        // OTHER
        const hasFavoriteService = anyHasActiveFeatures(
            devices,
            [ productFeatures.FAVORITES ]
        );
        const canGetFavoriteService = anyHasFeatureCapabilities(
            devices,
            [ productFeatures.FAVORITES ]
        );
        const hasHighlightService = anyHasActiveFeatures(
            devices,
            [ productFeatures.HIGHLIGHT_VIDEO ]
        );
        const canGetHighlightService = anyHasFeatureCapabilities(
            devices,
            [ productFeatures.HIGHLIGHT_VIDEO ]
        );

        filterItems.other = {
            items: []
        };

        addFilterItem(
            filterItems.other.items,
            eventAttributes.favorite,
            hasFavoriteService || canGetFavoriteService
        );

        addFilterItem(
            filterItems.other.items,
            eventAttributes.highlight,
            hasHighlightService || canGetHighlightService
        );

        return filterItems;
    }

    function anyHasSmartDetectionService(devices) {
        return devices.some(device => {
            if (device?.serviceStatus?.current) {
                return Object.keys(device.serviceStatus.current).some(service => {
                    if (serviceHasAnalytics(service)) {
                        return true;
                    }
                    return false;
                });
            }
            return false;
        });
    }

    function anyHasSmartDetectionCapability(devices) {
        return devices.some(device => {
            return Object.keys(device.availableFeatures).some(availableFeature => {
                return serviceHasAnalytics(availableFeature);
            });
        });
    }

    /**
     * @param {*} value
     * @returns {Array} Always returns an array, no matter the input.
     *     Objects with an id property are wrapped in an array.
     */
    function normalizeArray(value) {
        if (Array.isArray(value)) {
            return value;
        } else if (value && value.id) {
            return [ value ];
        }
        return [];
    }

    /**
     * @description Used to calculate filterItems.
     * @param {Array<Device>|Device} devices A Device or list of devices to check for active features
     * @param {Array<string>} features A list of service features that will cause a positive result
     * @returns {Boolean} true if at least one Device in devices has
     *     at least one of the provided features
     */
    function anyHasActiveFeatures(devices, features) {
        return normalizeArray(devices).some(device => {
            return features.some(feature => {
                return Boolean(device?.serviceStatus?.current?.[feature]);
            });
        });
    }

    /**
     * @description Used to calculate filterItems.
     * @param {Array<Device>|Device} devices A Device or list of devices to check for feature capabilities
     * @param {Array<string>} features A list of service features that will cause a positive result
     * @returns {Boolean} true if at least one Device in devices has
     *     the capability of getting at least one of the provided features
     */
    function anyHasFeatureCapabilities(devices, features) {
        return normalizeArray(devices).some(device => {
            return features.some(feature => {
                return device?.availableFeatures[feature] || device?.serviceStatus?.current[feature];
            });
        });
    }

    function clearSubject(subject) {
        if (subject?.getValue()) {
            subject.next();
        }
    }

    function clear() {
        selectedDevice = undefined;
        clearSubject(timezoneSubject);
        clearSubject(accessLevelsSubject);
        clearSubject(deviceUiSettingsSubject);
        clearSubject(eventInfoSubject);
        Object.keys(filterItemSubjects).forEach(key => {
            clearSubject(filterItemSubjects[key]);
            delete filterItemSubjects[key];
        });
        Object.keys(deviceSubjects).forEach(key => {
            clearSubject(deviceSubjects[key]);
            delete deviceSubjects[key];
        });
        util.clear(lightweight);
        util.clear(heavyweight);
        util.clear(requestedServices);
        util.clear(requestingServices);
        util.clear(gotDevices);
        util.clear(gotServices);
    }

    /**
     * @param {Object} params forwarded from a get request
     * @param {String} [params.id] set to a deviceId to request a single device, and the result will be a single device
     * @param {String} [params.context] contextEnum - "list", "forEvents", or "all"
     * @param {String} [params.ownerAlias] returns only devices that belong to a certain owner
     * @returns {BehaviorSubject} a behavior subject according to the given params
     */
    function getSubject(params) {
        if (params?.id) {
            deviceSubjects[params.id] ??= new BehaviorSubject();
            return deviceSubjects[params.id];
        }

        deviceSubjects[contextEnum.all] ??= new BehaviorSubject();

        let key;
        if (params) {
            key = (params.context || contextEnum.all) + (params.ownerAlias || '');
            if (key === contextEnum.all) {
                return deviceSubjects[contextEnum.all];
            }
        } else {
            return deviceSubjects[contextEnum.all];
        }

        if (!deviceSubjects[key]) {
            deviceSubjects[key] = new BehaviorSubject();
            deviceSubjects[contextEnum.all].subscribe(devicesArray => {
                if (Array.isArray(devicesArray)) {
                    deviceSubjects[key].next(
                        devicesArray
                            .filter(byContext, { context: params.context })
                            .filter(byOwnerAlias, { ownerAlias: params.ownerAlias })
                    );
                } else {
                    deviceSubjects[key].next();
                }
            });
        }
        return deviceSubjects[key];
    }

    function getData(params) {
        if (params?.services) {
            if (params.id) {
                requestingServices[params.id] = true;
            } else {
                requestingServices[contextEnum.all] = true;
            }
        }

        if (params?.heavy) {
            if (params.id) {
                if (!heavyweight[params.id]) {
                    heavyweight[params.id] = true;
                    getDeviceData(params);
                    return;
                }
            } else {
                if (!heavyweight[contextEnum.all]) {
                    heavyweight[contextEnum.all] = true;
                    getDeviceData(params);
                    return;
                }
            }
        } else {
            if (params?.id) {
                if (!lightweight[params.id] && !heavyweight[params.id]) {
                    lightweight[params.id] = true;
                    getDeviceData(params);
                    return;
                }
            } else {
                if (!lightweight[contextEnum.all] && !heavyweight[contextEnum.all]) {
                    lightweight[contextEnum.all] = true;
                    getDeviceData(params);
                    return;
                }
            }
        }

        importServices(params);
    }

    function applyRefreshParam(params) {
        if (params?.refresh) {
            if (params.id) {
                delete lightweight[params.id];
                delete heavyweight[params.id];
                delete requestingServices[params.id];
                delete requestedServices[params.id];
                delete gotDevices[params.id];
                delete gotServices[params.id];
            } else {
                util.clear(lightweight);
                util.clear(heavyweight);
                util.clear(requestingServices);
                util.clear(requestedServices);
                util.clear(gotDevices);
                util.clear(gotServices);
            }
        }
    }

    function activeService(device, serviceType) {
        if (typeof device?.serviceStatus?.current?.[serviceType] === 'object') {
            return device.serviceStatus.current[serviceType];
        }
    }

    function setUiSettings(device, uiSettingsDelta, traceId) {
        const toSave = util.merge(util.merge({}, device.uiSettings), uiSettingsDelta);
        return apiService.uiSettings.postForDevice({
            deviceId: device.id,
            toSave: toSave,
            traceId: traceId
        }).then(
            () => {
                device.uiSettings = toSave;
                getSubject({ id: device.id }).next(device);
            },
            () => {
                return Promise.reject();
            }
        );
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @property invoiceTypes
     * @name invoiceTypes
     */
    _deviceService.invoiceTypes = invoiceTypes; /* not used publically */

    /**
     * @property productFeatures
     * @name productFeatures
     */
    _deviceService.productFeatures = productFeatures;

    /**
     * @property serviceStates
     * @name serviceStates
     */
    _deviceService.serviceStates = serviceStates;

    /**
     * @property billingTypes
     * @name billingTypes
     */
    _deviceService.billingTypes = billingTypes;

    /**
     * @property contextEnum
     * @name contextEnum
     */
    _deviceService.contextEnum = contextEnum;

    /**
     * @property permissionsEnum
     * @name permissionsEnum
     */
    _deviceService.permissionsEnum = permissions;

    /**
     * @description Main method for retrieving devices
     * @param {Object} [params] specify how to get devices
     * @param {Boolean} [params.heavy] true to request extra device data needed for capabilities - slower
     * @param {String} [params.id] set to a deviceId to request a single device, and the result will be a single device
     * @param {Boolean} [params.services] true to get service related info - slower
     * @param {Boolean} [params.refresh] reload data from the server
     * @param {String} [params.context] contextEnum - "list", "forEvents", or "all"
     * @param {String} [params.ownerAlias] returns only devices that belong to a certain owner
     * @param {String} [params.guestCode] allows acces without a logged-in user
     * @param {String} [params.traceId] The SDK will create its own traceId if not provided.
     * @returns {Observable<Array<Device>>|Observable<Device>} subscribe to get updates on devices or a device
     * @name get
     * @method
     */
    _deviceService.get = function(params) {
        const internalParams = util.withTraceId(params);
        applyRefreshParam(internalParams);
        const result = getSubject(internalParams);
        getData(internalParams);
        return result.asObservable();
    };

    /**
     * @description used to get an instantaneous device value for logging purposes only.
     *     It only includes id and hardware info. Do not try to include in Observable chains.
     * @param {String} deviceId A deviceId
     * @returns {device} A stripped-down device that only includes id and hardware info
     * @name withHwOnly
     * @method
     */
    _deviceService.withHwOnly = function(deviceId) {
        if (deviceId) {
            const device = getSubject({ id: deviceId }).getValue();
            if (device) {
                return util.merge({}, { id: device.id, hw: device.hw });
            }
        }
    };

    /**
     * @param {Object} params
     * @param {Device} params.device A device object that should be deleted
     * @param {String} params.device.id
     * @param {String} [params.traceId]
     * @returns {Observable} completes after the remove completes and data is updated
     * @name remove
     * @method
     */
    _deviceService.remove = function(params) {
        return new Observable(subscriber => {
            if (!params?.device?.id) {
                subscriber.error(
                    log({
                        code: 'removeBadParams',
                        object: params,
                        traceId: params.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            apiService.devices.remove(internalParams).then(
                removeResult => {
                    _deviceService.get({
                        id: internalParams.device.id,
                        refresh: true,
                        traceId: removeResult?.traceId
                    }).pipe(take(1)).subscribe({
                        next() {
                            subscriber.next(internalParams.device.id);
                            subscriber.complete();
                        },
                        error: deviceRefreshError => subscriber.error(
                            log({
                                code: 'deviceRefreshAfterRemoveFail',
                                object: internalParams.device,
                                deviceId: internalParams.device?.id,
                                error: deviceRefreshError,
                                traceId: deviceRefreshError?.traceId
                            })
                        )
                    });
                },
                removeDeviceError => subscriber.error(
                    log({
                        code: 'removeDeviceFail',
                        object: internalParams.device,
                        deviceId: internalParams.device?.id,
                        error: removeDeviceError,
                        traceId: removeDeviceError?.traceId
                    })
                )
            );
        });
    };

    /**
     * @param {Device} device
     * @param {String} serviceType
     * @returns {Boolean} true if the device has active service
     * @name hasActiveService
     * @method
     */
    _deviceService.hasActiveService = function(device, serviceType) {
        return Boolean(activeService(device, serviceType));
    };

    /**
     * @param {Device} device
     * @returns {Boolean} true if the device currently has a trial
     * @name hasNonPaidService
     * @method
     */
    _deviceService.hasNonPaidService = function(device) {
        if (device?.serviceStatus?.current) {
            const current = device.serviceStatus.current;
            return Object.keys(current).some(key => {
                return current[key].invoiceType === invoiceTypes.trial;
            });
        }
        return false;
    };

    /**
     * @param {Device} device
     * @returns {Boolean} true if the device has paid service of some kind
     * @name hasPaidService
     * @method
     */
    _deviceService.hasPaidService = function(device) {
        if (device?.serviceStatus?.current) {
            const current = device.serviceStatus.current;
            return Object.keys(current).some(key => {
                return current[key].invoiceType === invoiceTypes.main
                    || current[key].invoiceType === invoiceTypes.prepay
                    || current[key].invoiceType === invoiceTypes.voucher;
            });
        }
        return false;
    };

    /**
     * @param {Device} device
     * @returns {Boolean} True if it is possbile for the camera to have paid service
     *     if the user purchases it
     * @method
     * @name couldHavePaidService
     */
    _deviceService.couldHavePaidService = function(device) {
        const paidProductTypes = [
            (productsPrefix + billingTypes.subscription),
            (productsPrefix + billingTypes.voucher),
            (productsPrefix + billingTypes.prepay)
        ];
        return paidProductTypes.some(function(paidProductArray) {
            return Array.isArray(device[paidProductArray])
                && device[paidProductArray].length > 0;
        });
    };

    /**
     * @deprecated - Apps can have their own concept of a seleted device,
     *    but the SDK should not care
     * @description gets or sets the selected device
     * @param {String|object} val deviceId or device with id
     * @returns {Device} The selected device
     * @name selected
     * @method
     */
    _deviceService.selected = function(val) {
        if (val === null) {
            updateSelectedDevice(undefined);
            return;
        }
        if (val !== undefined) {
            const id = ((typeof val === 'string') ? val : val.id);
            const device = getSubject({ id: id }).getValue();
            if (device) {
                // set selected synchronously if already loaded
                updateSelectedDevice(device);
            } else {
                // otherwise set selected asynchronously
                _deviceService.get({ id: id }).pipe(take(1)).subscribe({
                    next: d => updateSelectedDevice(d),
                    error: () => updateSelectedDevice(undefined)
                });
            }
        }
        return selectedDevice;
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable} observable array of timezones
     * @name getTimezones
     * @method
     */
    _deviceService.getTimezones = function(params) {
        if (!timezoneSubject.getValue() && !timezoneInProgress) {
            timezoneInProgress = true;
            const internalParams = util.withTraceId(params);
            apiService.timezones(internalParams).then(
                result => {
                    timezoneInProgress = false;
                    if (Array.isArray(result?.data?.timezones)) {
                        timezoneSubject.next(result.data.timezones);
                    } else {
                        clearSubject(timezoneSubject);
                    }
                },
                e => {
                    timezoneInProgress = false;
                    clearSubject(timezoneSubject);
                    log({
                        code: 'getTimezonesFailed',
                        error: e,
                        traceId: e?.traceId
                    });
                }
            );
        }
        return timezoneSubject.asObservable();
    };

    /**
     * @param {Object} params
     * @param {Device} params.device - A device to save settings for, which must include the id property.
     *     The entire device graph is not necessary - only the properties that have been modified need be inlcuded.
     * @param {String} params.device.id - the device to save settings for must have an id
     * @param {Object} params.device.settings - the settings to save
     * @param {String} [params.traceId]
     * @returns {Observable} - emits when the save is successful and data is updated
     * @name save
     * @method
     */
    _deviceService.save = function(params) {
        return new Observable(subscriber => {
            if (!params?.device?.id
                    || !params?.device?.settings
                    || typeof params?.device?.settings !== 'object') {
                subscriber.error(
                    log({
                        code: 'saveBadParams',
                        object: params,
                        deviceId: params?.device?.id,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            apiService.devices.save(internalParams).then(
                () => {
                    _deviceService.get({
                        id: internalParams.device.id,
                        refresh: true,
                        services: Boolean(requestedServices[internalParams.device.id]),
                        heavy: Boolean(heavyweight[internalParams.device.id]),
                        traceId: internalParams.traceId
                    }).pipe(util.takeFirstExisting).subscribe({
                        next: d => {
                            subscriber.next(d);
                            subscriber.complete();
                        },
                        error: e => subscriber.error(
                            log({
                                code: 'saveRefreshFail',
                                error: e,
                                deviceId: internalParams?.device?.id,
                                traceId: internalParams.traceId
                            })
                        )
                    });
                },
                e => subscriber.error(
                    log({
                        code: 'saveFail',
                        object: params,
                        deviceId: internalParams?.device?.id,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @description provides localized IR (infrared night vision) options for UI controls
     * @param {Device} device the device for IR options
     * @param {Object} [text] localization text for IR options
     * @param {String} [text.auto] localized auto text
     * @param {String} [text.on] localized on text
     * @param {String} [text.off] localized off text
     * @returns {Array} an array of options with label and value properties
     * @name irOptions
     * @method
     */
    _deviceService.irOptions = function(device, text) {
        const result = [];
        if (device?.cap?.cam?.ir) {
            result.push({
                label: text?.auto ?? 'auto',
                value: 2
            });
            result.push({
                label: text?.on ?? 'on',
                value: 1
            });
            result.push({
                label: text?.off ?? 'off',
                value: 0
            });
        }
        return result;
    };

    /**
     * @description provides localized camera rotation options for UI controls
     * @param {Device} device the device for IR options
     * @param {Object} [text] localization text for IR options
     * @param {String} [text.rightsideup] localized regular text
     * @param {String} [text.upsidedown] localized upsidedown text
     * @returns {Array} an array of options with label and value properties
     * @name rotationOptions
     * @method
     */
    _deviceService.rotationOptions = function(device, text) {
        const result = [];
        if (Array.isArray(device?.cap?.cam?.rotationList)) {
            device.cap.cam.rotationList.forEach(rotation => {
                if (rotation === 1) {
                    result.push({
                        label: text?.rightsideup ?? 'Right side up',
                        value: rotation
                    });
                } else if (rotation === 4) {
                    result.push({
                        label: text?.upsidedown ?? 'Upside down',
                        value: rotation
                    });
                } else {
                    // this code will normally never get called,
                    // but is here in case a camera has a new or wrong rotation option
                    result.push({
                        label: rotation,
                        value: rotation
                    });
                }
            });
        }
        return result;
    };

    /**
     * @description provides camera resolution options for UI controls
     * @param {Device} device the device to get resolution options for
     * @returns {Array} an array of options with label and value properties
     * @name resOptions
     * @method
     */
    _deviceService.resOptions = function(device) {
        const result = [];
        if (Array.isArray(device?.cap?.cam?.resList)) {
            device.cap.cam.resList.forEach(res =>
                result.push({
                    label: res.descr,
                    value: res.id
                })
            );
        }
        return result;
    };

    /**
     * @description provides camera frames per second options for UI controls
     * @param {Device} device the device to get frames per second options for
     * @returns {Array} an array of options with label and value properties
     * @name fpsOptions
     * @method
     */
    _deviceService.fpsOptions = function(device) {
        const result = [];
        if (device?.cap?.cam?.fpsMax) {
            let i = device.cap.cam.fpsMin;
            while (i <= device.cap.cam.fpsMax) {
                result.push({
                    label: i,
                    value: i
                });
                i += 1;
            }
        }
        return result;
    };

    /**
     * @param {string|object} device - a deviceId or device object
     * @param {String} permission - a permission to check
     * @returns {Boolean} true if the device has the permission or if there are no permissions given.
     * @name hasPermission
     * @method
     */
    _deviceService.hasPermission = function(device, permission) {
        let _device;
        if (typeof device === 'string') {
            _device = getSubject({ id: device }).getValue();
        } else {
            _device = device;
        }
        return _device && (_device.permissions[permission] || _device.permissions.noPermissions);
    };

    /**
     * @description Clients should call this when the status.hasEvents property
     *     might have changed on a device.
     *     AS of Jan 2023, only used internally with the SDK
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Observable} completes when hasEvents has been updated for all devices
     * @name refreshHasEvents
     * @method
     */
    _deviceService.refreshHasEvents = function(params) {
        return new Observable(subscriber => {
            const internalParams = util.withTraceId(params);
            forkJoin({
                user: userService.get(internalParams).pipe(util.takeFirstExistingWithNoError),
                eventInfo: getEventInfo({ refresh: true, traceId: internalParams.traceId }).pipe(util.takeFirstExistingWithNoError)
            }).subscribe({
                next: result => {
                    if (result.user.isError) {
                        subscriber.error(
                            log({
                                code: 'refreshHasEventsUserFail',
                                error: result.user.error,
                                traceId: internalParams.traceId
                            })
                        );
                        return;
                    }
                    if (result.eventInfo.isError) {
                        subscriber.error(
                            log({
                                code: 'refreshHasEventsEventInfoFail',
                                error: result.eventInfo.error,
                                traceId: internalParams.traceId
                            })
                        );
                        return;
                    }
                    const devices = getSubject().getValue();
                    if (devices) {
                        let updated = false;
                        devices.forEach(device => {
                            const newHasEvents = result.eventInfo.indexOf(device.id) !== -1;
                            if (newHasEvents !== device.status.hasEvents) {
                                device.status.hasEvents = newHasEvents;
                                // update a single device
                                getSubject({ id: device.id}).next(device);
                                updated = true;
                            }
                        });
                        // if one device changed, update all devices
                        if (updated) {
                            getSubject().next(devices);
                        }
                    }
                    subscriber.next();
                    subscriber.complete();
                },
                error: util.noop
            });
        });
    };

    /**
     * @param {Object} params specification object
     * spec.refresh is no longer needed since filterItems will update when devices are updated
     * @param {String} [params.ownerAlias] Only get devices that belong to a certain user,
     *     or leave undefined to consider all devices including shared
     * @param {String} [params.traceId]
     * @returns {Observable} next returns an array of filterItems
     * @name filterItems
     * @method
     */
    _deviceService.filterItems = function(params) {
        const internalParams = util.withTraceId(params);
        const key = internalParams.ownerAlias ? util.aliasWithNoGuidPrefix(internalParams.ownerAlias) : 'all';

        if (!filterItemSubjects[key]) {
            filterItemSubjects[key] = new BehaviorSubject();
            _deviceService.get({
                services: true,
                context: contextEnum.forEvents,
                ownerAlias: util.aliasWithNoGuidPrefix(internalParams.ownerAlias),
                traceId: internalParams.traceId
            }).subscribe({
                next: devices => {
                    if (devices) {
                        // only emit the next value if it got services for all devices
                        if (gotServices[contextEnum.all]) {
                            filterItemSubjects[key].next(calculateFilterItems(devices));
                        } else {
                            // if no services, then no filterItems,
                            // but only emit if there were filterItems previously
                            clearSubject(filterItemSubjects[key]);
                        }
                    } else {
                        // if no devices, then no filterItems,
                        // but only emit if there were filterItems previously
                        clearSubject(filterItemSubjects[key]);
                    }
                },
                error: e => {
                    log({
                        code: 'filterItemsFail',
                        traceId: internalParams.traceId,
                        error: e
                    });
                }
            });
        }
        return filterItemSubjects[key].asObservable();
    };

    /**
     * @param {Object} params
     * @param {String} params.id
     * @param {String} [params.traceId]
     * @returns {Observable} emits device firmware info
     * @name getFirmwareInfo
     * @method
     */
    _deviceService.getFirmwareInfo = function(params) {
        return new Observable(subscriber => {
            apiService.firmware.getInformation(params).then(
                result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                getFirmwareError => subscriber.error(
                    log({
                        code: 'getFirmwareInfo',
                        traceId: getFirmwareError.traceId,
                        deviceId: params?.id,
                        error: getFirmwareError
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.model camera model
     * @param {String} [params.traceId]
     * @returns {Observable} emits product info about the given camera model
     * @name productInfo
     * @method
     */
    _deviceService.productInfo = function(params) {
        if (!params?.model) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'productInfoBadParams',
                    object: params,
                    traceId: params?.traceId
                })
            ));
        }
        if (!productInfoSubjects[params.model]) {
            productInfoSubjects[params.model] = new BehaviorSubject();
            const internalParams = util.withTraceId(params);
            apiService.productImageData(internalParams).then(
                result => {
                    productInfoSubjects[params.model].next(result.data);
                },
                e => {
                    productInfoSubjects[params.model].error(
                        log({
                            code: 'unknownCameraModel',
                            object: params,
                            error: e,
                            traceId: e?.traceId
                        })
                    );
                    delete productInfoSubjects[params.model];
                }
            );
        }
        return productInfoSubjects[params.model].asObservable();
    };

    /**
     * @param {String} params
     * @param {String} params.deviceId
     * @param {Boolean} params.refresh
     * @param {String} params.size - values are: 'VGA', 'HD', 'FHD'
     * @param {String} [params.traceId]
     * @returns {Observable<ArrayBufer>} emits an ArrayBuffer containing a recent image from the device
     * @example To display an image to the user, follow these steps assuming the
     *     emitted value is imageDataResult:
     *     const arrayBufferView = new Uint8Array(imageDataResult);
     *     const blob = new Blob( [ arrayBufferView ], { type: 'image/jpeg' } );
     *     const urlCreator = _window.URL || _window.webkitURL;
     *     const imageUrl = urlCreator.createObjectURL( blob );
     *     myImage.src = imageUrl;
     * @name getRecentImage
     * @method
     */
    _deviceService.getRecentImage = function(params) {
        return new Observable(subscriber => {
            if (!params?.deviceId) {
                subscriber.error(
                    log({
                        code: 'getRecentImageBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            apiService.devices.getRecentImage(internalParams).then(
                result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                recentImageError => subscriber.error(
                    log({
                        code: 'getRecentImageFail',
                        object: params,
                        error: recentImageError,
                        deviceId: params?.deviceId,
                        traceId: recentImageError?.traceId
                    })
                )
            );
        });
    };

    /**
     * @param {String} params
     * @param {String} params.deviceId
     * @param {Boolean} params.refresh
     * @param {String} params.size - values are: 'VGA', 'HD', 'FHD'
     * @param {String} [params.traceId]
     * @returns {Observable<URL>} emits a URL of the device image
     * @name getRecentImageUrl
     * @method
     */
    _deviceService.getRecentImageUrl = function(params) {
        return new Observable(subscriber => {
            if (!params?.deviceId) {
                subscriber.error(
                    log({
                        code: 'getRecentImageUrlBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            _deviceService.getRecentImage(internalParams).subscribe({
                next: imageDataResult => {
                    const arrayBufferView = new Uint8Array(imageDataResult);
                    const blob = new Blob( [ arrayBufferView ], { type: 'image/jpeg' } );
                    const urlCreator = _window.URL ?? _window.webkitURL;
                    const imageUrl = urlCreator.createObjectURL( blob );
                    subscriber.next(imageUrl);
                    subscriber.complete();
                },
                error: e => subscriber.error(
                    log({
                        code: 'getRecentImageUrlFail',
                        object: params,
                        error: e,
                        deviceId: params?.deviceId,
                        traceId: e?.traceId
                    })
                )
            });
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.deviceId
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the view state has been reset
     * @name resetViewState
     * @method
     */
    // Not used as of December 2022, but should be used in a PTZ feature
    _deviceService.resetViewState = function(params) {
        return new Observable(subscriber => {
            if (!params?.deviceId) {
                subscriber.error(
                    log({
                        code: 'resetViewStateBadParams',
                        object: params,
                        traceId: params?.traceId,
                        deviceId: params?.deviceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            apiService.devices.resetViewState(internalParams).then(
                () => {
                    _deviceService.get({
                        refresh: true,
                        id: internalParams.deviceId,
                        heavy: true,
                        traceId: internalParams.traceId
                    })
                        .pipe(take(1))
                        .subscribe();
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'resetViewStateFail',
                        error: e,
                        traceId: e?.traceId,
                        deviceId: internalParams?.deviceId
                    })
                )
            );
        });
    };

    /**
     * @description Values for pan, tilt, zoom have different meanings depending on
     *     device.cap.cam.viewModes.camModeDefinitionList
     * @param {Object} params
     * @param {String} params.deviceId
     * @param {Number} params.p Pan
     * @param {Number} params.t Tilt
     * @param {Number} params.z Zoom
     * @param {Number} params.traceId
     * @returns {Observable} emits after the command was sent
     * @name setPtz
     * @method
     */
    // Not used as of December 2022, but should be used in a PTZ feature
    _deviceService.setPtz = function(params) {
        return new Observable(subscriber => {
            if (!params?.deviceId || !(params.p || params.t || params.z)) {
                subscriber.error(
                    log({
                        code: 'setPtzBadParams',
                        object: params,
                        deviceId: params?.deviceId,
                        traceId: params?.traceId
                    })
                );
            }
            apiService.devices.setPtz(params).then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'setPtzFail',
                        object: params,
                        error: e,
                        deviceId: params?.deviceId,
                        traceId: params?.traceId
                    })
                )
            );
        });
    };

    /**
     * @description Starts the firmware update process
     * @param {Object} params
     * @param {String} params.deviceId id of the device to update FW for
     * @param {String} [params.traceId]
     * @returns {Observable} completes when the firmware refresh has started and data has been refreshed
     * @name startFirmwareUpdate
     * @method
     */
    _deviceService.startFirmwareUpdate = function(params) {
        return new Observable(subscriber => {
            if (!params?.deviceId) {
                subscriber.error(
                    log({
                        code: 'fwUpdateBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            let startUpdateParams = util.withTraceId(params);
            apiService.firmware.startUpdate(startUpdateParams).then(
                firmwareUpdateResult => {
                    // force update of device data
                    _deviceService.get({
                        id: params.deviceId,
                        refresh: true,
                        heavy: true,
                        traceId: startUpdateParams.traceId
                    })
                        .pipe(take(1))
                        .subscribe();
                    subscriber.next(firmwareUpdateResult.data);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'startFirmwareUpdate',
                        deviceId: params.deviceId,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @description This can be used to enable/disable the controls for turning recording on/off
     * @param {Device} device
     * @returns {Boolean} true if the device can store videos for any length of time
     * @name supportsVideoStorage
     * @method
     */
    _deviceService.supportsVideoStorage = function(device) {
        return Boolean(device?.cap?.cam?.motionService?.storage.days);
    };

    /**
     * @deprecated
     * @param {Device} device
     * @returns {Boolean} true if the device should display named resolutions,
     *     or false to display 'width x height' format
     * @name hasResolutionNames
     * @method
     */
    _deviceService.hasResolutionNames = function(device) {
        if (Array.isArray(device?.cap?.cam?.resList) && device?.hw) {
            if ((device.hw.model
                    && modelsWithResolutionNames.indexOf(device.hw.model.toLowerCase()) >= 0)
                || (device.hw.partnerId
                    && partnersWithResolutionNames.indexOf(device.hw.partnerId.toLowerCase()) >= 0)
            ) {
                return true;
            }
        }
        return false;
    };

    /**
     * @param {String} deviceId Include a deviceId for only a single device,
     *     or do not inlcude a value to check all devices.
     * @returns {Boolean} true if services have been retrieved for the device or devices
     * @name gotServices
     * @method
     */
    _deviceService.gotServices = function(deviceId) {
        if (deviceId) {
            return Boolean(gotServices[deviceId]);
        }
        return Boolean(gotServices[contextEnum.all]);
    };

    /**
     * @description only used in legacy products
     * @param {Object} params
     * @param {Device} params.device
     * @param {String} params.device.id
     * @param {String} params.color hexadecimal color value without leading hash - "cc0000" for red
     * @param {String} [params.traceId]
     * @name setColor
     * @method
     */
    _deviceService.setColor = function(params) {
        return new Observable(subscriber => {
            if (!params?.device || !params?.device?.id || !params?.color || !colorRegex.test(params?.color)) {
                subscriber.error(
                    log({
                        code: 'setColorBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            if (setColorInProgress[params.device.id]) {
                subscriber.error('in progress');
                return;
            }
            setColorInProgress[params.device.id] = true;
            const internalParams = util.withTraceId(params);
            setUiSettings(
                internalParams.device,
                { color: internalParams.color },
                internalParams.traceId
            ).then(
                () => {
                    delete setColorInProgress[internalParams.device.id];
                    subscriber.next(internalParams.color);
                    subscriber.complete();
                },
                e => {
                    delete setColorInProgress[internalParams.device.id];
                    subscriber.error(
                        log({
                            code: 'setColorFail',
                            error: e,
                            traceId: internalParams.traceId,
                            deviceId: internalParams?.device?.id
                        })
                    );
                }
            );
        });
    };

    /**
     * @description Not used in the web app as of Jan 2023, but is used internally within the SDK
     * @returns {Boolean} true if all devices have been loaded
     * @name gotAllDevices
     * @method
     */
    _deviceService.gotAllDevices = function() {
        return Boolean(gotDevices[contextEnum.all]);
    };

    /**
     * @description Before using this method, get the device with the parameter set 'heavy=true'
     *     so that the device will have enough info to get aspect ratio from.
     *     Can't calculate aspect ratio without knowing device.cap.cam.resList or device.settings.cam.res.
     * @param {Device} device
     * @returns {Number} The current aspect ratio of the device = width / height
     * @name aspectRatio
     * @method
     */
    _deviceService.aspectRatio = function(device) {
        const resListCapability = device?.cap?.cam?.resList;
        const resSetting = device?.settings?.cam?.res;
        if (resListCapability && resListCapability.length > 0 && resSetting) {
            // first try looking up the resolution in the resList
            // this is the most proper way to get aspect ratio
            const currentResolution = resListCapability.find(r => r.id === resSetting);
            if (currentResolution) {
                return currentResolution.w / currentResolution.h;
            }
            // if that fails, just parse the resolution setting as a backup
            const dimensionsFromId = resSetting.split('x');
            if (dimensionsFromId.length === 2) {
                return parseInt(dimensionsFromId[0], 10) / parseInt(dimensionsFromId[1], 10);
            }
            // if that fails, use the first resListCapability
            return resListCapability[0].w / resListCapability[0].h;
        }
        if (resSetting) {
            const dimensionsFromId = resSetting.split('x');
            if (dimensionsFromId.length === 2) {
                return parseInt(dimensionsFromId[0], 10) / parseInt(dimensionsFromId[1], 10);
            }
        }
        if (resListCapability && resListCapability.length > 0) {
            return resListCapability[0].w / resListCapability[0].h;
        }
    };

    // Any time loggedIn emits false, run clear
    apiService.loggedIn().pipe(filter(l => !l)).subscribe(() => clear());

    return Object.freeze(_deviceService);
}

export default createDeviceService;
